/** GeoJson utility functions for converting coordinates between ints and floats. Some precision
    may be lost by rounding when converting from floats to ints. */

/** Multiply all numbers in the n-dimensional array by the multiplier
 *  @param {*} val
 *  @param {Number} multiplier
 *  @param {Number} decimalPlaces - Number of decimal places to round to
 *  @return {*} - Probably should return a coordinate array converted to ints or back to floats
 */
const _multiplyCoordinates = (val, multiplier, decimalPlaces) => {
  if (Array.isArray(val))
  { return val.map((curr) => _multiplyCoordinates(curr, multiplier, decimalPlaces)); }
  const result = val * multiplier;
  const fixedResult = result.toFixed(decimalPlaces);
  return parseFloat(fixedResult);
};

/** Return the number of decimal places for the supplied number
 *  @param {Number} number
 *  @return {Number}
 */
const _countDecimalPlaces = (number) => {
  const numberSplit = number.toString().split('.');
  return (numberSplit[1] && numberSplit[1].length) || 0;
};

/** Determine the largest number of decimal places present among all values in the n-dimensional array
 *  @param {*} val - Number or an n-dimensional array of numbers
 *  @return {Number}
 */
const _getMaxDecimalPlaces = (val) => {
  if (Array.isArray(val)) {
    return val.reduce((maxDecimalPlaces, curr) =>
      Math.max(_getMaxDecimalPlaces(curr), maxDecimalPlaces), 0);
  }
  return _countDecimalPlaces(val);
};

/** Convert the given coordinate values to ints. If a coordinate value is a floating point with
 *  >12 decimal places, it will be rounded to the nearest 12th decimal during the int conversion.
 *  @param {Array} coordinates
 *  @return {Object}
 */
export const convertCoordinatesToInts = (coordinates) => {
  const MAX_DECIMAL_PLACES = 12;
  let decimalPlaces = _getMaxDecimalPlaces(coordinates);
  if (decimalPlaces > MAX_DECIMAL_PLACES) { decimalPlaces = MAX_DECIMAL_PLACES; }
  return {
    decimalPlaces,
    intCoordinates: _multiplyCoordinates(coordinates, Math.pow(10, decimalPlaces), 0)
  };
};

/** Convert the given coordinate values back to floats
 *  @param {Array} coordinates
 *  @param {Number} decimalPlaces
 *  @return {Array}
 */
export const convertCoordinatesBackToFloats = (coordinates, decimalPlaces) => {
  return _multiplyCoordinates(coordinates, Math.pow(10, decimalPlaces * -1), decimalPlaces);
};
